import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'
import TripFormModal from '../components/TripFormModal'
import TripDetailModal from '../components/TripDetailModal'
import { useSubscription } from '../lib/useSubscription'
import { openCheckout } from '../lib/paddle'

const teal = '#0D9488'
const tealLight = '#14B8A6'
const plum = '#1E1040'

export default function Mileage() {
  const { t } = useTranslation()
  const { canWrite } = useSubscription()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [trips, setTrips] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [search, setSearch] = useState('')
  const [monthFilter, setMonthFilter] = useState('current')
  const [vehicleFilter, setVehicleFilter] = useState('all')
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [detailTrip, setDetailTrip] = useState(null)

  useEffect(() => {
    loadAll()
    const addParam = searchParams.get('add')
    if (addParam === '1' || addParam === 'trip') {
      setFormModalOpen(true)
      searchParams.delete('add')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const [{ data: tripsData }, { data: vehData }] = await Promise.all([
      supabase.from('mileage_trips').select('*').eq('user_id', user.id).order('trip_date', { ascending: false }),
      supabase.from('vehicles').select('*').eq('user_id', user.id).order('is_default', { ascending: false }),
    ])

    setTrips(tripsData || [])
    setVehicles(vehData || [])
    setLoading(false)
  }

  const handleSaved = (trip) => {
    setTrips(prev => {
      const existingIdx = prev.findIndex(tr => tr.id === trip.id)
      if (existingIdx >= 0) {
        const copy = [...prev]
        copy[existingIdx] = trip
        return copy.sort((a, b) => (b.trip_date || '').localeCompare(a.trip_date || ''))
      }
      return [trip, ...prev].sort((a, b) => (b.trip_date || '').localeCompare(a.trip_date || ''))
    })
  }

  const handleUpdated = (updated) => {
    setTrips(prev => prev.map(tr => tr.id === updated.id ? updated : tr))
    setDetailTrip(updated)
  }

  const handleDeleted = (id) => {
    setTrips(prev => prev.filter(tr => tr.id !== id))
  }

  const now = new Date()
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const filtered = trips.filter(tr => {
    if (monthFilter === 'current') {
      if (tr.trip_date < currentMonthStart || tr.trip_date > currentMonthEnd) return false
    } else if (monthFilter !== 'all') {
      if (!tr.trip_date?.startsWith(monthFilter)) return false
    }
    if (vehicleFilter !== 'all' && tr.vehicle_id !== vehicleFilter) return false
    if (search) {
      const q = search.toLowerCase()
      const from = (tr.from_address || '').toLowerCase()
      const to = (tr.to_address || '').toLowerCase()
      const purpose = (tr.purpose || '').toLowerCase()
      if (!from.includes(q) && !to.includes(q) && !purpose.includes(q)) return false
    }
    return true
  })

  const monthsSet = new Set()
  trips.forEach(tr => { if (tr.trip_date) monthsSet.add(tr.trip_date.slice(0, 7)) })
  const months = Array.from(monthsSet).sort().reverse()

  const totalMiles = filtered.reduce((sum, tr) => sum + parseFloat(tr.miles || 0), 0)
  const totalDeduction = filtered.reduce((sum, tr) => sum + parseFloat(tr.deduction_amount || 0), 0)

  // Locale-aware formatters
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  const formatCurrency = (n) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0)
  const formatMiles = (n) => (n || 0).toLocaleString(locale, { maximumFractionDigits: 1 })
  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  const formatMonth = (ym) => new Date(ym + '-01T00:00:00').toLocaleDateString(locale, { month: 'long', year: 'numeric' })

  const shortenAddr = (addr) => {
    if (!addr) return ''
    return addr.split(',')[0].trim()
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{t('mileage.title')}</h1>
          <p style={styles.subtitle}>{t('mileage.subtitle')}</p>
        </div>
        <button
          style={{ ...styles.addBtn, opacity: canWrite ? 1 : 0.55 }}
          onClick={() => {
            if (!canWrite) {
              ;(async () => {
                const { data: { user } } = await supabase.auth.getUser()
                if (user) {
                  await openCheckout({
                    userEmail: user.email,
                    userId: user.id,
                    onSuccess: () => setTimeout(() => window.location.reload(), 2500),
                  })
                }
              })()
              return
            }
            setFormModalOpen(true)
          }}
        >
          {!canWrite ? `🔒 ${t('mileage.subscribe_to_log')}` : `+ ${t('mileage.log_trip')}`}
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filtersBar}>
        <div style={styles.searchField}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder={t('mileage.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <select
          style={styles.filterSelect}
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="current">{t('mileage.this_month')}</option>
          <option value="all">{t('mileage.all_time')}</option>
          {months.map(m => (
            <option key={m} value={m}>{formatMonth(m)}</option>
          ))}
        </select>
        {vehicles.length > 1 && (
          <select
            style={styles.filterSelect}
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
          >
            <option value="all">{t('mileage.all_vehicles')}</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.nickname || v.make_model || t('mileage.vehicle_default')}</option>
            ))}
          </select>
        )}
      </div>

      {/* Totals strip */}
      <div style={styles.totalsStrip}>
        <div style={styles.totalsLeft}>
          <div style={styles.totalsLabel}>
            {t('mileage.count_label', { count: filtered.length })}
            {monthFilter === 'current' && ` ${t('mileage.this_month_lower')}`}
            {monthFilter !== 'all' && monthFilter !== 'current' && ` ${t('mileage.in_month', { month: formatMonth(monthFilter) })}`}
          </div>
          <div style={styles.totalsMiles}>{t('mileage.miles_total', { miles: formatMiles(totalMiles) })}</div>
        </div>
        <div style={styles.totalsValue}>{formatCurrency(totalDeduction)}</div>
      </div>

      {/* List */}
      {loading ? (
        <div style={styles.loading}>{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasAny={trips.length > 0}
          onAdd={() => setFormModalOpen(true)}
          t={t}
        />
      ) : (
        <div style={styles.list}>
          {filtered.map(tr => (
            <button key={tr.id} style={styles.row} onClick={() => setDetailTrip(tr)}>
              <div style={styles.rowIconBox}>
                <span style={styles.rowIcon}>🚗</span>
              </div>
              <div style={styles.rowMain}>
                <div style={styles.rowRoute}>
                  {shortenAddr(tr.from_address)} <span style={styles.arrow}>→</span> {shortenAddr(tr.to_address)}
                </div>
                <div style={styles.rowMeta}>
                  <span>{formatDate(tr.trip_date)}</span>
                  <span style={styles.rowDot}>·</span>
                  <span>{t('mileage.miles_short', { miles: formatMiles(tr.miles) })}</span>
                  {tr.round_trip && <span style={styles.rowBadge}>{t('mileage.round_trip_badge')}</span>}
                  {tr.purpose && (
                    <>
                      <span style={styles.rowDot}>·</span>
                      <span style={styles.rowPurpose}>{tr.purpose}</span>
                    </>
                  )}
                </div>
              </div>
              <div style={styles.rowAmount}>{formatCurrency(tr.deduction_amount)}</div>
            </button>
          ))}
        </div>
      )}

      {/* Modals */}
      {formModalOpen && (
        <TripFormModal
          onClose={() => setFormModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
      {detailTrip && (
        <TripDetailModal
          trip={detailTrip}
          vehicles={vehicles}
          onClose={() => setDetailTrip(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

function EmptyState({ hasAny, onAdd, t }) {
  return (
    <div style={styles.empty}>
      <div style={styles.emptyIcon}>🚗</div>
      <div style={styles.emptyTitle}>
        {hasAny ? t('mileage.empty_filters_title') : t('mileage.empty_title')}
      </div>
      <div style={styles.emptySub}>
        {hasAny ? t('mileage.empty_filters_sub') : t('mileage.empty_sub')}
      </div>
      {!hasAny && (
        <button style={styles.emptyBtn} onClick={onAdd}>
          + {t('mileage.log_first')}
        </button>
      )}
    </div>
  )
}

const styles = {
  page: { padding: '32px 40px 80px', maxWidth: '1080px', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', gap: '16px', flexWrap: 'wrap' },
  title: { fontFamily: "'Fraunces', serif", fontSize: '36px', fontWeight: 900, letterSpacing: '-1px', color: plum, margin: 0, lineHeight: 1.1 },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '6px', fontWeight: 500 },
  addBtn: {
    background: teal, color: '#042f2e', border: 'none',
    padding: '11px 22px', borderRadius: '100px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  filtersBar: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', marginBottom: '14px' },
  searchField: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#9CA3AF', pointerEvents: 'none' },
  searchInput: {
    width: '100%', padding: '11px 14px 11px 38px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    background: '#fff', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum, boxSizing: 'border-box',
  },
  filterSelect: {
    padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    background: '#fff', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum, cursor: 'pointer', fontWeight: 500,
  },
  totalsStrip: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px',
    background: `linear-gradient(135deg, ${teal}, ${tealLight})`,
    borderRadius: '14px', marginBottom: '16px', color: '#fff',
  },
  totalsLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  totalsLabel: { fontSize: '13px', fontWeight: 600, opacity: 0.95 },
  totalsMiles: { fontSize: '13px', fontWeight: 600, opacity: 0.85 },
  totalsValue: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, letterSpacing: '-0.3px' },
  loading: { padding: '60px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },
  list: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', overflow: 'hidden' },
  row: {
    display: 'grid', gridTemplateColumns: 'auto 1fr auto',
    gap: '14px', padding: '14px 18px',
    borderTop: '1px solid #F3F4F6', alignItems: 'center',
    width: '100%', background: '#fff', border: 'none',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'background 0.15s',
  },
  rowIconBox: {
    width: '44px', height: '44px', borderRadius: '10px',
    background: '#F9FAFB', border: '1px solid #E5E7EB',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  rowIcon: { fontSize: '22px' },
  rowMain: { minWidth: 0 },
  rowRoute: { fontSize: '14px', fontWeight: 700, color: plum, marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  arrow: { color: teal, margin: '0 6px', fontWeight: 800 },
  rowMeta: { fontSize: '12px', color: '#9CA3AF', fontWeight: 500, display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  rowDot: { color: '#D1D5DB' },
  rowBadge: {
    fontSize: '10px', fontWeight: 700, color: teal,
    background: 'rgba(13, 148, 136, 0.1)',
    padding: '2px 8px', borderRadius: '4px',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  rowPurpose: { color: '#6B7280' },
  rowAmount: { fontFamily: "'Fraunces', serif", fontSize: '16px', fontWeight: 800, color: plum },
  empty: { background: '#fff', border: '1px dashed #D1D5DB', borderRadius: '16px', padding: '56px 32px', textAlign: 'center' },
  emptyIcon: { fontSize: '56px', marginBottom: '14px' },
  emptyTitle: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: plum, marginBottom: '8px', letterSpacing: '-0.3px' },
  emptySub: { fontSize: '14px', color: '#6B7280', marginBottom: '22px', fontWeight: 500 },
  emptyBtn: {
    background: teal, color: '#042f2e', border: 'none',
    padding: '13px 28px', borderRadius: '100px',
    fontSize: '15px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 6px 20px ${teal}40`,
  },
}
